import { useBrandLocale } from '../../hooks/useBrandLocale';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Button } from '../../components/button';
import { Alert, Badge, LoadingBlock, Muted, Stack, Strong } from '../../components/ui';
import {
  useCreateStayDraftMutation,
  usePublishStayMutation,
  useStayPublishChecklistQuery,
} from '../../services/stayService/__generated__/queries.generated';
import { toPublicS3ObjectUrl } from '../../utils/s3PublicUrl';
import {
  EMPTY_ADDRESS,
  StayAddressForm,
  addressProblem,
  toAddressInput,
  type StayAddress,
} from './StayAddressForm';
import { PropertyTypeSection, RoomTypeSection, StaySectionFields } from './StayFormSections';
import { StayPhotosEditor } from './StayPhotosEditor';
import { PROBLEM_STEP, publishProblemsOf, stayErrorMessage } from './stayErrors';
import {
  SECTION_FIELDS,
  WIZARD_STEPS,
  emptyStayForm,
  listingSummary,
  nextWizardStep,
  previousWizardStep,
  resumeWizardStep,
  wizardStepIndex,
  type StayForm,
  type StayListing,
  type WizardStepId,
} from './stayForm';
import { useStayEditor } from './useStayEditor';

export const LISTINGS_PATH = '/stays/host/listings';
export const listingEditPath = (id: string, step?: WizardStepId) =>
  `${LISTINGS_PATH}/${id}/edit${step ? `?step=${step}` : ''}`;
export const listingManagePath = (id: string) => `${LISTINGS_PATH}/${id}`;

const Shell = styled.div`
  ${tw`flex flex-col`}
  min-height: calc(100vh - 80px);
  background: var(--color-bg-elevated, #fff);
`;

const TopBar = styled.div`
  ${tw`flex items-center justify-between px-4 py-3 sm:px-8`}
  border-bottom: 1px solid var(--color-border);
`;

const Body = styled.main`
  ${tw`flex-1 w-full px-4 pt-8 pb-32 mx-auto sm:px-6`}
  max-width: 680px;
`;

const StepTitle = styled.h1`
  ${tw`text-2xl font-semibold sm:text-3xl`}
  color: var(--color-text-primary);
  font-family: var(--font-display);
  letter-spacing: -0.01em;
`;

const StepDescription = styled.p`
  ${tw`mt-2 mb-8 text-base`}
  color: var(--color-text-secondary);
`;

const Footer = styled.footer`
  ${tw`fixed bottom-0 left-0 right-0 z-10`}
  background: var(--color-bg-elevated, #fff);
`;

const Progress = styled.div<{ $value: number }>`
  height: 6px;
  background: var(--color-bg-secondary);
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({ $value }) => `${Math.round($value * 100)}%`};
    background: var(--color-text-primary);
    transition: width 0.3s ease;
  }
`;

const FooterRow = styled.div`
  ${tw`flex items-center justify-between px-4 py-4 mx-auto sm:px-8`}
  max-width: 1120px;
`;

const PreviewCard = styled.div`
  ${tw`overflow-hidden`}
  border-radius: var(--radius-card, 12px);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-card);
  img {
    ${tw`object-cover w-full`}
    aspect-ratio: 4 / 3;
  }
`;

const PreviewBody = styled.div`
  ${tw`flex flex-col gap-1 p-4`}
`;

const ProblemLink = styled.button`
  ${tw`p-0 text-sm font-medium underline cursor-pointer`}
  border: none;
  background: none;
  color: var(--color-text-primary);
`;

function ReviewStep({
  listing,
  onGoTo,
  checklist,
}: {
  listing: StayListing;
  onGoTo: (step: WizardStepId) => void;
  checklist: { code: string; message: string }[] | null;
}) {
  const cover = listing.images?.[0]?.imageUrl;
  return (
    <Stack $gap={6}>
      <PreviewCard data-testid="stay-review-preview">
        {cover && <img src={toPublicS3ObjectUrl(cover)} alt="Cover" />}
        <PreviewBody>
          <Strong>{listing.name || 'Untitled place'}</Strong>
          <Muted $size="sm">{listingSummary(listing)}</Muted>
          {listing.weekdayPrice ? <Muted $size="sm">${listing.weekdayPrice} night</Muted> : null}
        </PreviewBody>
      </PreviewCard>
      {checklist === null ? (
        <LoadingBlock label="Checking your listing" />
      ) : checklist.length === 0 ? (
        <Alert tone="success" title="Ready to publish">
          Guests can find and book your place as soon as you publish.
        </Alert>
      ) : (
        <Alert tone="warning" title="Before you publish">
          <Stack $gap={4} data-testid="stay-publish-problems">
            {checklist.map(problem => (
              <div key={problem.code}>
                {problem.message}.{' '}
                {PROBLEM_STEP[problem.code] && (
                  <ProblemLink type="button" onClick={() => onGoTo(PROBLEM_STEP[problem.code])}>
                    Fix
                  </ProblemLink>
                )}
              </div>
            ))}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

/**
 * The host's listing wizard (spec 016 W6): 14 steps, one screen each. The
 * first three (type, room, address) build the draft; from then on every Next
 * saves that step with `updateStay` and records the step reached, so Save &
 * exit and a later visit resume where the host stopped.
 */
export default function ListingWizardPage() {
  const brandCountry = useBrandLocale().country;
  const { id } = useParams<{ id?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const editor = useStayEditor(id);
  const { listing, form, errors, change, save, saving } = editor;

  // Before the draft exists the first three steps live in local state.
  const [draftForm, setDraftForm] = useState<StayForm>(emptyStayForm);
  const [address, setAddress] = useState<StayAddress>(EMPTY_ADDRESS);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  const [createDraft, { loading: creating }] = useCreateStayDraftMutation();
  const [publishStay, { loading: publishing }] = usePublishStayMutation();

  const requested = searchParams.get('step') as WizardStepId | null;
  const step: WizardStepId = useMemo(() => {
    if (!id) {
      return requested && wizardStepIndex(requested) >= 0 && wizardStepIndex(requested) <= 2
        ? requested
        : 'property-type';
    }
    if (requested && wizardStepIndex(requested) >= 0) return requested;
    return resumeWizardStep(listing?.stayDetails?.wizardStep);
  }, [id, requested, listing?.stayDetails?.wizardStep]);
  const stepDef = WIZARD_STEPS[wizardStepIndex(step)];

  const checklistQuery = useStayPublishChecklistQuery({
    variables: { id: id ?? '' },
    skip: !id || step !== 'review',
    fetchPolicy: 'network-only',
  });

  // The address of an existing draft, for going back to the location step.
  useEffect(() => {
    if (!listing?.location) return;
    setAddress(previous =>
      previous.latitude !== null
        ? previous
        : {
            street: listing.location?.address?.split(',')[0] ?? '',
            unit: listing.location?.address?.split(',').slice(1).join(',').trim() ?? '',
            city: listing.location?.city ?? '',
            state: listing.location?.state ?? '',
            zipCode: listing.location?.zipCode ?? '',
            latitude: listing.latitude ?? null,
            longitude: listing.longitude ?? null,
          },
    );
  }, [listing]);

  const goTo = (target: WizardStepId) => {
    setStepError(null);
    if (id) {
      setSearchParams({ step: target });
    } else {
      setSearchParams(target === 'property-type' ? {} : { step: target });
    }
    window.scrollTo?.(0, 0);
  };

  const activeForm = id ? form : draftForm;
  const onChange = (patch: Partial<StayForm>) =>
    id ? change(patch) : setDraftForm(previous => ({ ...previous, ...patch }));

  const failed = (message?: string): false => {
    if (message) setStepError(message);
    return false;
  };

  /** Saves the current step; true when the host can move on. */
  const saveStep = async (reached: WizardStepId | null): Promise<boolean> => {
    setStepError(null);
    const wizardStep = reached ?? undefined;
    if (!id) {
      if (step !== 'location') return true;
      const problem = addressProblem(address, brandCountry);
      if (problem) {
        setAddressError(problem);
        return false;
      }
      try {
        const { data } = await createDraft({
          variables: {
            input: {
              propertyType: draftForm.propertyType,
              roomType: draftForm.roomType,
              address: toAddressInput(address),
            },
          },
        });
        const created = data?.createStayDraft;
        if (created) navigate(listingEditPath(created.id, 'basics'), { replace: true });
        return false;
      } catch (error) {
        setStepError(stayErrorMessage(error, 'Could not create your listing'));
        return false;
      }
    }

    if (step === 'property-type') {
      const typeResult = await save(['propertyType'], { wizardStep });
      return typeResult.ok || failed(typeResult.message);
    }
    if (step === 'room-type') {
      const roomResult = await save(['roomType'], { wizardStep });
      return roomResult.ok || failed(roomResult.message);
    }
    if (step === 'location') {
      const problem = addressProblem(address, brandCountry);
      if (problem) {
        setAddressError(problem);
        return false;
      }
      const result = await save([], { address: toAddressInput(address), wizardStep });
      return result.ok || failed(result.message);
    }
    if (stepDef.section) {
      const result = await save(SECTION_FIELDS[stepDef.section], { wizardStep });
      return result.ok || failed(result.message);
    }
    // Photos and review have their own saves; still record the step reached.
    if (reached) {
      const result = await save([], { wizardStep });
      return result.ok || failed(result.message);
    }
    return true;
  };

  const onNext = async () => {
    const next = nextWizardStep(step);
    if (await saveStep(next)) {
      if (next) goTo(next);
    }
  };

  const onBack = () => {
    const previous = previousWizardStep(step);
    if (previous) goTo(previous);
  };

  const onSaveAndExit = async () => {
    if (id && (await saveStep(step))) navigate(LISTINGS_PATH);
    if (!id) navigate(LISTINGS_PATH);
  };

  const onPublish = async () => {
    if (!id) return;
    setStepError(null);
    try {
      await publishStay({ variables: { id } });
      toast.success('Your place is listed');
      navigate(listingManagePath(id));
    } catch (error) {
      const problems = publishProblemsOf(error);
      if (problems.length) {
        checklistQuery.refetch();
      }
      setStepError(stayErrorMessage(error, 'Could not publish'));
    }
  };

  if (id && editor.loading) {
    return (
      <Shell>
        <Body>
          <LoadingBlock label="Loading your listing" />
        </Body>
      </Shell>
    );
  }
  if (id && editor.loadError && !listing) {
    return (
      <Shell>
        <Body>
          <Alert tone="error" title="Listing not found">
            {stayErrorMessage(editor.loadError, 'This listing could not be loaded.')}
          </Alert>
        </Body>
      </Shell>
    );
  }

  const progress = (wizardStepIndex(step) + 1) / WIZARD_STEPS.length;
  const isLast = step === 'review';
  const checklist = checklistQuery.data?.stayPublishChecklist ?? null;

  return (
    <Shell data-testid="stay-wizard">
      <TopBar>
        <Badge tone="neutral" size="sm" data-testid="stay-wizard-step">
          Step {wizardStepIndex(step) + 1} of {WIZARD_STEPS.length}
        </Badge>
        <Button
          type="button"
          theme="secondary"
          size="sm"
          text="Save & exit"
          onClick={onSaveAndExit}
          isLoading={saving}
          dataTestId="stay-wizard-exit"
        />
      </TopBar>
      <Body>
        <StepTitle>{stepDef.title}</StepTitle>
        <StepDescription>{stepDef.description}</StepDescription>
        {stepError && (
          <Box>
            <Alert tone="error" title="Not saved">
              {stepError}
            </Alert>
          </Box>
        )}
        {step === 'property-type' && (
          <PropertyTypeSection form={activeForm} errors={errors} onChange={onChange} />
        )}
        {step === 'room-type' && (
          <RoomTypeSection form={activeForm} errors={errors} onChange={onChange} />
        )}
        {step === 'location' && (
          <StayAddressForm
            value={address}
            onChange={next => {
              setAddress(next);
              setAddressError(null);
            }}
            error={addressError}
          />
        )}
        {step === 'photos' && listing && <StayPhotosEditor listing={listing} />}
        {stepDef.section && (
          <StaySectionFields
            section={stepDef.section}
            form={activeForm}
            errors={errors}
            onChange={onChange}
          />
        )}
        {step === 'review' && listing && (
          <ReviewStep listing={listing} onGoTo={goTo} checklist={checklist} />
        )}
      </Body>
      <Footer>
        <Progress $value={progress} aria-hidden />
        <FooterRow>
          {previousWizardStep(step) ? (
            <Button type="button" theme="text" text="Back" onClick={onBack} />
          ) : (
            <span />
          )}
          {isLast ? (
            <Button
              type="button"
              text="Publish"
              onClick={onPublish}
              isLoading={publishing}
              isDisabled={!checklist || checklist.length > 0}
              dataTestId="stay-wizard-publish"
            />
          ) : (
            <Button
              type="button"
              text={!id && step === 'location' ? 'Create listing' : 'Next'}
              onClick={onNext}
              isLoading={saving || creating}
              dataTestId="stay-wizard-next"
            />
          )}
        </FooterRow>
      </Footer>
    </Shell>
  );
}

const Box = styled.div`
  ${tw`mb-6`}
`;
